// Side-effect-free on purpose: `config.ts` holds the `import.meta.glob` loader,
// which the Playwright loader can't parse. Keeping the constants here lets E2E
// specs import the real list instead of duplicating it.
import type { ParseKeys } from "i18next";

export const SUPPORTED_LOCALES = [
  "en",
  "fr",
  "es",
  "pt-BR",
  "de",
  "it",
  "pl",
  "ru",
  "tr",
] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";

/** Native language names — shown untranslated in the selector (industry standard). */
export const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  fr: "Français",
  es: "Español",
  "pt-BR": "Português (Brasil)",
  de: "Deutsch",
  it: "Italiano",
  pl: "Polski",
  ru: "Русский",
  tr: "Türkçe",
};

export const isSupportedLocale = (value: string): value is Locale =>
  (SUPPORTED_LOCALES as ReadonlyArray<string>).includes(value);

/**
 * Narrows the raw `i18n.resolvedLanguage` — a plain string i18next may not
 * have resolved yet — to a supported locale, English otherwise. The one rule
 * for everything that hands the app locale to a third party (the API, Stripe).
 */
export const resolveLocale = (active: string | undefined): Locale =>
  active && isSupportedLocale(active) ? active : DEFAULT_LOCALE;

/** The browser's preferred languages, most-preferred first. */
export const browserLanguages = (): ReadonlyArray<string> =>
  navigator.languages.length ? navigator.languages : [navigator.language];

/**
 * Every key `en.json` ships. `ParseKeys` rather than `keyof typeof en.json`:
 * i18next derives the CLDR plural suffixes itself, so the base
 * `catalog_show_episodes_count` — which the JSON only ships as `_one`/`_other`
 * — stays assignable. See CLAUDE.md (Localization) for where to apply it.
 */
export type TranslationKey = ParseKeys;
